import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Icon } from '@shared/components/icon/icon.enum';
import { ValidatorType } from '@shared/models/enums/validator-type.enum';
import { MultiMatrix } from '@shared/models/question-types/multi-matrix';
import { getFormGroup } from '@shared/utils/form.util';
import { SafeHtmlPipe } from '../../../../../../shared/pipes/safe-html.pipe';
import { ToValidIdPipe } from '../../../../../../shared/pipes/to-valid-id.pipe';
import { ValidatorComponent } from '../../../validator/validator.component';
import { MultiSelectComponent } from '../../multi-select/multi-select.component';
import { OpenAnswerInputComponent } from '../../open-answer/open-answer-input/open-answer-input.component';
import { SingleSelectDropdownInputComponent } from '../../single-select-questions/single-select-dropdown/single-select-dropdown-input/single-select-dropdown-input.component';
import { SingleSelectRadioButtonComponent } from '../../single-select-questions/single-select/single-select-radio-button/single-select-radio-button.component';
import { SliderComponent } from '../../single-select-questions/slider/slider.component';
import { MultiMatrixTableHeadComponent } from './multi-matrix-table-head/multi-matrix-table-head.component';

@Component({
  selector: 'app-multi-matrix-table',
  imports: [
    SingleSelectRadioButtonComponent,
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    SliderComponent,
    MultiMatrixTableHeadComponent,
    ValidatorComponent,
    OpenAnswerInputComponent,
    SingleSelectDropdownInputComponent,
    MultiSelectComponent,
    SafeHtmlPipe,
    ToValidIdPipe,
  ],
  templateUrl: './multi-matrix-table.component.html',
  styleUrl: './multi-matrix-table.component.scss',
})
export class MultiMatrixTableComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  private static readonly OVERFLOW_THRESHOLD_PX = 10;

  @Input({ required: true }) multiMatrixQuestion!: MultiMatrix;
  @Input({ required: true }) form!: FormGroup;

  @ViewChild('tableContainer') tableContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('scrollbarTrack') scrollbarTrack?: ElementRef<HTMLDivElement>;
  @ViewChild('stickyHeader') stickyHeader?: ElementRef<HTMLDivElement>;
  @ViewChild('stickyInner') stickyInner?: ElementRef<HTMLDivElement>;

  isMobileView: boolean;
  Icon = Icon;
  ValidatorType = ValidatorType;
  getFormGroup = getFormGroup;

  get allRowsHaveRequiredError(): boolean {
    const allRows = [
      ...this.multiMatrixQuestion.columns[0].rows,
      ...this.multiMatrixQuestion.columns[1].rows,
    ];
    return (
      allRows.length > 0 &&
      allRows.every((row) => {
        const control = this.form.get(row.id);
        return (
          (control?.hasError('required') ||
            control?.hasError('atLeastOneRequired')) &&
          control?.touched
        );
      })
    );
  }

  // Custom scrollbar properties
  hasHorizontalOverflow = false;
  scrollbarThumbWidth = 100;
  scrollbarThumbPosition = 0;
  firstColumnWidth = 0;
  headerFirstRowHeight = 0;
  private headerHeight = 0;
  private tableWidth = 0;
  private isDragging = false;
  private dragStartX = 0;
  private scrollStartX = 0;
  private scrollParent?: HTMLElement | Window;
  private resizeObserver?: ResizeObserver;
  private readonly onLayoutScroll = () => {
    this.updateStickyPosition();
    this.handleScrollWithBlur();
  };
  private readonly onWindowResize = () => {
    this.updateScrollbarDimensions();
    this.updateStickyPosition();
  };

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {
    this.isMobileView = window.innerWidth < 768;
  }

  ngOnInit(): void {
    window.addEventListener('resize', this.onResize.bind(this));
  }

  ngAfterViewInit(): void {
    this.scrollParent = this.getScrollParent();
    if (this.scrollParent instanceof Window) {
      window.addEventListener('scroll', this.onLayoutScroll, { passive: true });
    } else {
      this.scrollParent?.addEventListener('scroll', this.onLayoutScroll, {
        passive: true,
      });
    }
    window.addEventListener('resize', this.onWindowResize);

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateScrollbarDimensions();
        this.updateStickyPosition();
      });

      const container = this.tableContainer?.nativeElement;
      if (container) {
        this.resizeObserver.observe(container);
      }

      const table = container?.querySelector('table');
      if (table) {
        this.resizeObserver.observe(table);
      }
    }

    this.checkOverflow();

    setTimeout(() => {
      this.setRemoveScrollbar();
      // Initialize scrollbar after it's rendered
      setTimeout(() => {
        this.updateScrollbarDimensions();
        this.updateStickyPosition();
        this.handleScrollWithBlur();
      }, 50);
    }, 100);

    // Listen for recalculate overflow events from child components
    this.elementRef.nativeElement.addEventListener(
      'recalculate-overflow',
      this.handleRecalculateOverflow.bind(this),
    );
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onResize.bind(this));
    window.removeEventListener('resize', this.onWindowResize);
    if (this.scrollParent instanceof Window) {
      window.removeEventListener('scroll', this.onLayoutScroll);
    } else {
      this.scrollParent?.removeEventListener('scroll', this.onLayoutScroll);
    }
    this.resizeObserver?.disconnect();
    this.elementRef.nativeElement.removeEventListener(
      'recalculate-overflow',
      this.handleRecalculateOverflow.bind(this),
    );
  }

  onResize(event: Event): void {
    this.isMobileView = (event.target as Window).innerWidth < 768;
    if (!this.isMobileView) {
      setTimeout(() => {
        this.updateScrollbarDimensions();
        this.onTableScroll();
        this.updateStickyPosition();
        this.handleScrollWithBlur();
      }, 100);
    }
  }

  handleRecalculateOverflow(): void {
    setTimeout(() => {
      this.setRemoveScrollbar();
      this.updateScrollbarDimensions();
      this.onTableScroll();
      this.updateStickyPosition();
      this.handleScrollWithBlur();
    }, 200);
  }

  checkOverflow(): void {
    setTimeout(() => {
      this.setRemoveScrollbar();
    }, 100);
  }

  /**
   * Checks if table content has horizontal overflow and updates custom scrollbar.
   */
  private setRemoveScrollbar(): void {
    setTimeout(() => {
      const container =
        this.elementRef.nativeElement.querySelector('.table-container');

      if (!container) {
        return;
      }

      const overflowAmount =
        container.scrollWidth -
        (container.clientWidth +
          MultiMatrixTableComponent.OVERFLOW_THRESHOLD_PX);
      const hasOverflow = overflowAmount > 0;

      this.hasHorizontalOverflow = hasOverflow;

      if (hasOverflow) {
        this.updateScrollbarDimensions();
      }

      this.cdr.detectChanges();
    }, 100);
  }

  private updateScrollbarDimensions(): void {
    const container = this.tableContainer?.nativeElement;
    const track = this.scrollbarTrack?.nativeElement;

    if (!container) {
      return;
    }

    const firstColumnCell = container.querySelector('tbody tr th:first-child');
    if (firstColumnCell) {
      this.firstColumnWidth = firstColumnCell.getBoundingClientRect().width;
    }

    const headerFirstRow = container.querySelector('thead tr:first-child');
    if (headerFirstRow) {
      this.headerFirstRowHeight = headerFirstRow.getBoundingClientRect().height;
    }

    const thead = container.querySelector('thead');
    if (thead) {
      this.headerHeight = thead.getBoundingClientRect().height;
      if (!this.headerHeight) {
        const rows = Array.from(thead.querySelectorAll('tr'));
        this.headerHeight = rows.reduce(
          (sum, row) => sum + row.getBoundingClientRect().height,
          0,
        );
      }
    }

    const table = container.querySelector('table');
    if (table) {
      this.tableWidth = table.getBoundingClientRect().width;
    }

    this.elementRef.nativeElement.style.setProperty(
      '--matrix-first-column-width',
      `${Math.round(this.firstColumnWidth)}px`,
    );
    this.elementRef.nativeElement.style.setProperty(
      '--matrix-header-height',
      `${Math.round(this.headerHeight)}px`,
    );
    this.elementRef.nativeElement.style.setProperty(
      '--matrix-table-width',
      `${Math.round(this.tableWidth || container.scrollWidth)}px`,
    );

    const effectiveTrackWidth = container.clientWidth - this.firstColumnWidth;
    const scrollableWidth = container.scrollWidth - this.firstColumnWidth;
    const visibleRatio = effectiveTrackWidth / scrollableWidth;

    if (track) {
      this.scrollbarThumbWidth = Math.max(40, track.clientWidth * visibleRatio);
    }

    this.syncStickyColumnWidths();

    this.updateStickyHorizontal();
  }

  onTableScroll(): void {
    this.handleScrollWithBlur();

    if (this.isDragging) {
      this.updateStickyHorizontal();
      return;
    }

    const container = this.tableContainer?.nativeElement;
    const track = this.scrollbarTrack?.nativeElement;

    if (!container || !track) {
      return;
    }

    const scrollRatio =
      container.scrollLeft / (container.scrollWidth - container.clientWidth);
    const maxThumbPosition = track.clientWidth - this.scrollbarThumbWidth;
    this.scrollbarThumbPosition = scrollRatio * maxThumbPosition;

    this.updateStickyHorizontal();
  }

  private handleScrollWithBlur(): void {
    const wrapper = this.elementRef.nativeElement.querySelector('.matrix-wrapper');
    const container = this.tableContainer?.nativeElement;
    const stickyHeader = this.stickyHeader?.nativeElement;

    if (!wrapper || !container) return;

    const hasHorizontalScroll = container.scrollLeft > 0;
    const hasVerticalStickyActive = stickyHeader?.classList.contains('is-active');

    if (hasHorizontalScroll || hasVerticalStickyActive) {
      wrapper.classList.add('is-scrolling');
    } else {
      wrapper.classList.remove('is-scrolling');
    }
  }

  onScrollbarThumbMouseDown(event: MouseEvent): void {
    event.preventDefault();
    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.scrollStartX = this.scrollbarThumbPosition;
  }

  onScrollbarTrackClick(event: MouseEvent): void {
    const track = this.scrollbarTrack?.nativeElement;
    const container = this.tableContainer?.nativeElement;

    if (!track || !container) {
      return;
    }

    if ((event.target as HTMLElement).classList.contains('custom-scrollbar-thumb')) {
      return;
    }

    const trackRect = track.getBoundingClientRect();
    const clickPosition = event.clientX - trackRect.left;
    const maxThumbPosition = track.clientWidth - this.scrollbarThumbWidth;
    const newThumbPosition = Math.max(
      0,
      Math.min(clickPosition - this.scrollbarThumbWidth / 2, maxThumbPosition),
    );

    const scrollRatio = newThumbPosition / maxThumbPosition;
    container.scrollLeft =
      scrollRatio * (container.scrollWidth - container.clientWidth);
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) {
      return;
    }

    const track = this.scrollbarTrack?.nativeElement;
    const container = this.tableContainer?.nativeElement;

    if (!track || !container) {
      return;
    }

    const deltaX = event.clientX - this.dragStartX;
    const maxThumbPosition = track.clientWidth - this.scrollbarThumbWidth;
    const newThumbPosition = Math.max(
      0,
      Math.min(this.scrollStartX + deltaX, maxThumbPosition),
    );

    this.scrollbarThumbPosition = newThumbPosition;

    const scrollRatio = newThumbPosition / maxThumbPosition;
    container.scrollLeft =
      scrollRatio * (container.scrollWidth - container.clientWidth);
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this.isDragging = false;
  }

  private updateStickyHorizontal(): void {
    const container = this.tableContainer?.nativeElement;
    const stickyInner = this.stickyInner?.nativeElement;
    const sticky = this.stickyHeader?.nativeElement;

    if (!container || !stickyInner) {
      return;
    }

    const baseLeft = -container.scrollLeft;
    stickyInner.style.transform = 'none';
    stickyInner.style.left = `${baseLeft}px`;
    stickyInner.style.width = `${this.tableWidth || container.scrollWidth}px`;

    if (sticky) {
      const sourceLine =
        container.querySelector(
          'thead tr:nth-child(2) th.second-column-start',
        ) ?? container.querySelector('thead th.second-column-start');
      const cloneLine =
        sticky.querySelector(
          'thead tr:nth-child(2) th.second-column-start',
        ) ?? sticky.querySelector('thead th.second-column-start');

      if (sourceLine && cloneLine) {
        const sourceRect = sourceLine.getBoundingClientRect();
        const cloneRect = cloneLine.getBoundingClientRect();
        const delta = sourceRect.left - cloneRect.left;

        if (Math.abs(delta) > 0.1) {
          stickyInner.style.left = `${baseLeft + delta}px`;
        }
      }
    }
  }

  private updateStickyPosition(): void {
    const container = this.tableContainer?.nativeElement;
    const sticky = this.stickyHeader?.nativeElement;
    const scrollParent = this.scrollParent;

    if (!container || !sticky || !scrollParent) {
      return;
    }

    const table = container.querySelector('table');
    if (!table) {
      return;
    }

    const isWindow = scrollParent instanceof Window;
    const stickyOffset = 0;
    const layoutRectTop = isWindow
      ? 0
      : (scrollParent as HTMLElement).getBoundingClientRect().top;
    const tableRect = table.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const thead = table.querySelector('thead');
    const theadRect = thead?.getBoundingClientRect();
    const scrollTop = isWindow
      ? window.scrollY
      : (scrollParent as HTMLElement).scrollTop;
    const viewportHeight = isWindow
      ? window.innerHeight
      : (scrollParent as HTMLElement).clientHeight;
    const tableTop = tableRect.top - layoutRectTop + scrollTop;
    const tableBottom = tableTop + tableRect.height;
    const headerHeight = this.headerHeight || 0;
    const headerTop =
      theadRect?.top !== undefined
        ? theadRect.top - layoutRectTop + scrollTop
        : tableTop;

    const canScrollVertically = tableRect.height > viewportHeight;
    const shouldShow =
      canScrollVertically &&
      headerHeight > 0 &&
      scrollTop >= headerTop + stickyOffset &&
      scrollTop <= tableBottom - headerHeight;

    sticky.style.setProperty('--matrix-sticky-offset', `${stickyOffset}px`);
    sticky.style.top = `${layoutRectTop}px`;
    sticky.style.left = `${containerRect.left}px`;
    sticky.style.width = `${containerRect.width}px`;
    sticky.style.height = `${headerHeight + stickyOffset}px`;

    if (shouldShow) {
      sticky.classList.add('is-active');
      this.syncStickyColumnWidths();
      this.updateStickyHorizontal();
    } else {
      sticky.classList.remove('is-active');
    }
  }

  private syncStickyColumnWidths(): void {
    const container = this.tableContainer?.nativeElement;
    const sticky = this.stickyHeader?.nativeElement;

    if (!container || !sticky) {
      return;
    }

    const sourceHeader = container.querySelector('thead');
    const cloneHeader = sticky.querySelector('thead');
    let sourceColGroups = container.querySelectorAll('colgroup');
    let cloneColGroups = sticky.querySelectorAll('colgroup');

    if (!sourceHeader || !cloneHeader) {
      return;
    }

    const cloneTable = sticky.querySelector('table');
    const sourceRow2 = sourceHeader.querySelector('tr:nth-child(2)');
    if (cloneTable && sourceRow2) {
      const row2Cells = Array.from(sourceRow2.querySelectorAll('th'));
      if (row2Cells.length > 0) {
        const cornerCell = sourceHeader.querySelector(
          'tr:first-child th:first-child',
        ) as HTMLElement | null;
        const firstColWidth =
          cornerCell?.getBoundingClientRect().width ?? this.firstColumnWidth;

        const secondStartIndex = row2Cells.findIndex((cell) =>
          cell.classList.contains('second-column-start'),
        );
        const firstGroupCells =
          secondStartIndex > -1 ? row2Cells.slice(0, secondStartIndex) : row2Cells;
        const secondGroupCells =
          secondStartIndex > -1 ? row2Cells.slice(secondStartIndex) : [];

        const buildGroup = (
          cells: HTMLElement[],
          addBorder: boolean,
        ): HTMLTableColElement => {
          const group = document.createElement('colgroup');
          if (addBorder) {
            group.style.borderLeft = '1px solid rgb(107, 114, 128)';
          }
          for (const cell of cells) {
            const col = document.createElement('col');
            const width = cell.getBoundingClientRect().width;
            col.style.width = `${width}px`;
            col.style.minWidth = `${width}px`;
            col.style.maxWidth = `${width}px`;
            group.appendChild(col);
          }
          return group;
        };

        const groups: HTMLTableColElement[] = [];
        if (firstColWidth > 0) {
          const labelGroup = document.createElement('colgroup');
          const labelCol = document.createElement('col');
          labelCol.style.width = `${firstColWidth}px`;
          labelCol.style.minWidth = `${firstColWidth}px`;
          labelCol.style.maxWidth = `${firstColWidth}px`;
          labelGroup.appendChild(labelCol);
          groups.push(labelGroup);
        }
        if (firstGroupCells.length > 0) {
          groups.push(buildGroup(firstGroupCells, false));
        }
        if (secondGroupCells.length > 0) {
          groups.push(buildGroup(secondGroupCells, false));
        }

        const existingColGroups = Array.from(
          cloneTable.querySelectorAll('colgroup'),
        );
        existingColGroups.forEach((group) => group.remove());

        const insertBeforeNode = cloneTable.firstChild;
        groups.forEach((group) => {
          cloneTable.insertBefore(group, insertBeforeNode);
        });

        sourceColGroups = container.querySelectorAll('colgroup');
        cloneColGroups = sticky.querySelectorAll('colgroup');
      }
    }

    if (
      sourceColGroups.length > 0 &&
      cloneColGroups.length === sourceColGroups.length
    ) {
      for (let i = 0; i < sourceColGroups.length; i += 1) {
        const sourceCols = Array.from(
          sourceColGroups[i].querySelectorAll('col'),
        );
        const cloneCols = Array.from(
          cloneColGroups[i].querySelectorAll('col'),
        );

        if (sourceCols.length > 0 && cloneCols.length === sourceCols.length) {
          for (let j = 0; j < sourceCols.length; j += 1) {
            const width =
              (sourceCols[j] as HTMLElement).getBoundingClientRect().width;
            const cloneCol = cloneCols[j] as HTMLElement;
            cloneCol.style.width = `${width}px`;
            cloneCol.style.minWidth = `${width}px`;
            cloneCol.style.maxWidth = `${width}px`;
          }
        }
      }
    }

    const sourceCells = Array.from(sourceHeader.querySelectorAll('th'));
    const cloneCells = Array.from(cloneHeader.querySelectorAll('th'));
    const count = Math.min(sourceCells.length, cloneCells.length);

    for (let i = 0; i < count; i += 1) {
      const sourceCell = sourceCells[i];
      const cloneCell = cloneCells[i] as HTMLElement;
      const sourceWidth = sourceCell.getBoundingClientRect().width;

      cloneCell.style.setProperty('width', `${sourceWidth}px`, 'important');
      cloneCell.style.setProperty('min-width', `${sourceWidth}px`, 'important');
      cloneCell.style.setProperty('max-width', `${sourceWidth}px`, 'important');
    }
  }

  private getScrollParent(): HTMLElement | Window {
    let parent = this.elementRef.nativeElement as HTMLElement | null;

    while (parent) {
      parent = parent.parentElement;
      if (!parent) {
        break;
      }

      const style = getComputedStyle(parent);
      if (/(auto|scroll)/.test(style.overflowY)) {
        return parent;
      }
    }

    return window;
  }
}
